import React, { useEffect, useState, useCallback } from 'react';

import moment from 'moment';
import { Skeleton } from '@mui/material';
import { CommonDateOnlyFormat } from 'helpers/common.helper';
import Accordion from 'react-bootstrap/Accordion';
import {
	AttachmentIcon,
    TimelineTitleLoaderIcon,
} from 'ui-component/custom-icons/icons';

import CreatedImageIcon from 'assets/images/timeline/created.png';
import PaidImageIcon from 'assets/images/timeline/paid.png';
import PreparingImageIcon from 'assets/images/timeline/preparing.png';
import ShipmentImageIcon from 'assets/images/timeline/shipment.png';
import SalesOrdermageIcon from 'assets/images/timeline/salesorder.png';
import { MdKeyboardArrowDown } from 'react-icons/md';
import PurchaseAdviceModal from 'ui-component/PurchaseAdviceModal';
import useIsMobile from 'utils/isMobileUtils';


// ✅ Clear
function Timeline({ refreshKey, id, source, pendingQty, productOverviewCount }) {

    const [data, setData] = useState([]);
	const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [sortedSteps, setSortedSteps] = useState([]);
    const [invoiceOrderArrangement, setInvoiceOrderArrangement] = useState(0);
    const [salesOrderArrangement, setSalesOrderArrangement] = useState(0);
    const [paymentArrangement, setPaymentArrangement] = useState(0);
    const [showAllSalesOrder, setShowAllSalesOrder] = useState(false);
    const [showAllInvoices, setShowAllInvoices] = useState(false);
    const [showAllPayments, setShowAllPayments] = useState(false);
    const [showAllPreparing, setShowAllPreparing] = useState(false);
    const [showAllShipments, setShowAllShipments] = useState(false);
    const [showPurchaseAdvice, setShowPurchaseAdvice] = useState(false);
    const [shippedQuantity, setShippedQuantity] = useState(0);
    const [totalOrderQuantity, setTotalOrderQuantity] = useState(0);


    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);

    const handleAccordionToggle = () => {
        if (!isOpen) {
            setShowAllSalesOrder(false);
            setShowAllInvoices(false);
            setShowAllPayments(false);
            setShowAllPreparing(false);
            setShowAllShipments(false);
        }
        setIsOpen(!isOpen);
    };

    const fetchData = useCallback(async () => {
		const request = {
			id: id,
			type: source
		}
		api.getTimeline(request).then((res) => {
			if (res?.status) {
				setData(res?.data);
                setIsDataLoaded(true);
                const invoiceDate = moment(res?.data?.invoice?.create?.date);
                const salesOrderDate = moment(res?.data?.sales_order?.create?.date);
                const paymentDate = moment(res?.data?.payments?.create?.date);

                let orderQty = 0;
                let purchasedQty = 0
                if (source === 'invoice') {
                    if (res?.data?.invoice?.create?.status) {
                        orderQty = res?.data?.invoice?.create?.details?.total_qty;
                    } else if (res?.data?.sales_order?.create?.status) {
                        orderQty = res?.data?.sales_order?.create?.details?.total_qty;
                    }
                } else {
                    if (res?.data?.sales_order?.create?.status) {
                        orderQty = res?.data?.sales_order?.create?.details?.total_qty;
                    } else if (res?.data?.invoice?.create?.status) {
                        orderQty = res?.data?.invoice?.create?.details?.total_qty;
                    }
                }
                if (res?.data?.operation?.processing?.status) {
                    const getPurchaseQuantity = res?.data?.operation?.processing?.purchase_orders?.flatMap(po =>
                            Array.isArray(po?.details)
                                ? po.details.map(detail => detail?.qty)
                                : []
                        );
                    const totalPurchasedQty = getPurchaseQuantity.reduce((sum, qty) => sum + (qty || 0), 0);
                    purchasedQty = totalPurchasedQty;
                }

                if (res?.data?.operation?.shipment?.status) {
                    if (res?.data?.operation?.shipment?.total > 0) {
                        setShippedQuantity(res?.data?.operation?.shipment?.total);
                    }
                }
                setTotalOrderQuantity(res?.data?.invoice?.create?.details?.total_qty || res?.data?.sales_order?.create?.details?.total_qty || 0);
              



                const steps = [
                    {
                        key: 'sales_order',
                        label: 'Sales Order',
                        date: salesOrderDate,
                        icon: SalesOrdermageIcon,
                        active: res?.data?.sales_order?.create?.status,
                    },
                    {
                        key: 'invoice',
                        label: 'Invoice',
                        date: invoiceDate,
                        icon: CreatedImageIcon,
                        active: res?.data?.invoice?.create?.status,
                    },
                    {
                        key: 'paid',
                        label: 'Paid',
                        date: paymentDate,
                        icon: PaidImageIcon,
                        active: res?.data?.payments?.create?.date ? true : false,
                        completed: true,
                    },
                ];

                const filteredAndSortedSteps = steps
                    .filter(step => step.date)
                    .sort((a, b) => {
                        // If sales order status is false, always put it last
                        if (a.key === 'sales_order' && !a.active) return 1;
                        if (b.key === 'sales_order' && !b.active) return -1;
                        // Otherwise, sort by date
                        return a.date - b.date;
                    });
                
                setSortedSteps(filteredAndSortedSteps);

                // Set arrangement indexes
                filteredAndSortedSteps.forEach((step, idx) => {
                    if (step.key === 'invoice') setInvoiceOrderArrangement(idx);
                    if (step.key === 'sales_order') setSalesOrderArrangement(idx);
                    if (step.key === 'paid') setPaymentArrangement(idx);
                });
                
			} else {
			}
		});

    }, [refreshKey, id]);


	const viewData = (type, id) => {
		if (type === 'quotation') {
			window.open(`/sales/quotations/${id}`, '_blank');
		} else if (type === 'invoice') {
			window.open(`/sales/invoices/${id}`, '_blank');
		} else if (type === 'sales_order') {
			window.open(`/sales/sales-orders/${id}`, '_blank');
        } else if (type === 'contacts') {
			window.open(`/contacts/${id}`, '_blank');
            
        }
    };

    useEffect(() => {
        fetchData()
    }, [id]);

    const timelineSections = [
        {
            key: 'invoice',
            arrangement: invoiceOrderArrangement,
            content: (
                <div className="mt10">
                    <div className="row">
                        <div className={`col-12 flex gap-3 align-items-center mb10 timeline-section-header ${ data?.invoice?.create?.status && 'active' }`}>
                            <div>
                                <img src={CreatedImageIcon} height={'40px'} />
                            </div>
                            <div className="drawer-title-container" style={{ marginLeft: '10px', letterSpacing: '0.3px' }}>
                                Invoice created
                            </div>
                            <div>

                                <div className={`badge badge-primary`} style={{ width: 'auto', right: '0' }}>
                                    <span className="fw-300">Total QTY</span> 
                                    <span style={{ marginLeft: '5px', fontWeight: '600' }}>{ data?.invoice?.create?.details?.total_qty }</span>
                                </div>
                            </div>
                            
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                                        
                            <div className="rb-container relative">
                                <ul className="rb">
                                    {(() => {
                                        const items = [
                                            <li className={`rb-item ${ data?.invoice?.create?.status && 'active' }`} ng-repeat="itembx">
                                                <div className="timestamp">
                                                    <div className="drawer-title-container">{ data?.invoice?.create?.status ? data?.invoice?.create?.title : 'Invoice created' }</div>
                                                    {
                                                        data?.invoice?.create?.status && (
                                                            <div className="drawer-date-container">{ moment(data?.invoice?.create?.date).format(CommonDateOnlyFormat) }</div>
                                                        )
                                                    }
                                                </div>
                                                <div className="item-content text-gray-2">
                                                    
                                                    { 
                                                        data?.invoice?.create?.status ? (
                                                            data?.invoice?.create?.details?.source === 'invoice' ? (
                                                                <span>
                                                                    { data?.invoice?.create?.details?.reference } has been created.
                                                                </span>
                                                            ) : (
                                                                <span>
                                                                    { data?.invoice?.create?.details?.reference } has been processed under { data?.invoice?.create?.details?.source } <span className="text-primary cursor-pointer" onClick={() => { viewData(data?.invoice?.create?.details?.source, data?.invoice?.create?.details?.id) }}>{ data?.invoice?.create?.details?.reference } </span>.
                                                                </span>
                                                            )
                                                        ) : (
                                                            'No details'
                                                        )
                                                    }
                                                    
                                                </div>
                                            </li>,
                                            <li className={`rb-item ${ data?.invoice?.approved?.status && 'active' }`} ng-repeat="itembx">
                                                <div className="timestamp">
                                                    <div className="drawer-title-container">{ data?.invoice?.approved?.status ? data?.invoice?.approved?.title : 'Approved' }</div>
                                                    {
                                                        data?.invoice?.approved?.status && (
                                                            <div className="drawer-date-container">{ moment(data?.invoice?.approved?.date).format(CommonDateOnlyFormat) }</div>
                                                        )
                                                    }
                                                </div>
                                                <div className="item-content">{ data?.invoice?.approved?.status ? `${data?.invoice?.approved?.message}.` : 'No details' }</div>
                                            </li>
                                        ].filter(Boolean);

                                        if (!showAllInvoices) {
                                            return items.slice(0, 1);
                                        }
                                        return items;
                                    })()}

                                    
                                </ul>

                                <div className="absolute" style={{ marginTop: '-20px', left: '27px' }}>
                                    <a href="javascript:void(0);"
                                        style={{ fontSize: '12px', padding: 0 }}
                                        onClick={() => setShowAllInvoices(!showAllInvoices)}
                                    >
                                        {showAllInvoices ? 'View less' : 'View more'}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: 'payment',
            arrangement: paymentArrangement,
            content: (
                <div className="mt10">
                    <div className="row">
                        <div className={`col-12 flex gap-3 align-items-center mb10 timeline-section-header ${data?.payments?.create?.date && 'active' }`}>
                            <div>
                                <img src={PaidImageIcon} height={'40px'} />
                            </div>
                            <div className="drawer-title-container" style={{ marginLeft: '10px', letterSpacing: '0.3px' }}>
                                Payment
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                                        
                            <div className="rb-container">
                                <ul className="rb">
                                    {
                                        data?.payments?.create?.payments?.length > 0 ? (
                                            (() => {
                                                const items = data?.payments?.create?.payments?.map((pd, index) => (
                                                    <li className={`rb-item timeline-progress-layer-text active`} ng-repeat="itembx">
                                                        <div className="timestamp">
                                                            <div className="drawer-title-container">{pd?.title}</div>
                                                        </div>
                                                        <div className="item-content text-gray-2">
                                                            Created on {moment(pd?.date).format(CommonDateOnlyFormat)}
                                                        </div>
                                                        <div className="item-content text-gray-2">
                                                            <div>
                                                                {
                                                                    pd?.credit_note_id ? (
                                                                        <>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                        </>
                                                                    )
                                                                }
                                                                {pd?.message}
                                                            </div>
                                                        </div>
                                                    </li>
                                                )) || [];

                                                if (!showAllPayments) {
                                                    return items.slice(0, 1);
                                                }
                                                return items;
                                            })()
                                        ) : (
                                            <li className={`rb-item timeline-progress-layer-text ${data?.invoice?.payment?.status && 'active'}`} ng-repeat="itembx">
                                                <div className="timestamp">
                                                    <div className="drawer-title-container">Payment received</div>
                                                </div>
                                                <div className="item-content">
                                                    No details
                                                </div>
                                            </li>
                                        )
                                    }
                                </ul>
                                {
                                    data?.payments?.create?.payments?.length > 0 && (
                                            <div className="absolute" style={{ marginTop: '-20px', left: '27px' }}>
                                                <a href="javascript:void(0);"
                                                    style={{ fontSize: '12px', padding: 0 }}
                                                    onClick={() => setShowAllPayments(!showAllPayments)}
                                                >
                                                    {showAllPayments ? 'View less' : 'View more'}
                                                </a>
                                            </div>
                                    )
                                }
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: 'sales_order',
            arrangement: salesOrderArrangement,
            content: (
                <div className="mt10">

                    <div className="row">
                        <div className={`col-12 flex gap-3 align-items-center mb10 timeline-section-header ${ data?.sales_order?.create?.status && 'active' }`}>
                            <div>
                                <img src={SalesOrdermageIcon} height={'40px'} />
                            </div>
                            <div className="drawer-title-container" style={{ marginLeft: '10px', letterSpacing: '0.3px' }}>
                                Sales Order created
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                                        
                            <div className="rb-container relative">
                                <ul className="rb">

                            {(() => {
                                const items = [
                                    <li className={`rb-item timeline-progress-layer-text ${ data?.sales_order?.create?.status && 'active' }`} ng-repeat="itembx">
                                        <div className="timestamp">
                                            <div className="drawer-title-container">{ data?.sales_order?.create?.status ? data?.sales_order?.create?.title : 'Sales order created' }</div>
                                            {
                                                data?.sales_order?.create?.status && (
                                                    <div className="drawer-date-container">{ moment(data?.sales_order?.create?.date).format(CommonDateOnlyFormat) }</div>
                                                )
                                            }
                                        </div>
                                        <div className="item-content text-gray-2">
                                            
                                            { 
                                                data?.sales_order?.create?.status ? (
                                                    data?.sales_order?.create?.details?.source === 'quotation' ? (
                                                        <>
                                                            { data?.sales_order?.create?.details?.quotation_number } was created under quotation <span className="text-primary cursor-pointer" onClick={() => { viewData('quotation', data?.sales_order?.create?.details?.id) }}>{ data?.sales_order?.create?.details?.quotation_number }</span>.
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="text-primary cursor-pointer" onClick={() => { viewData('sales_order', data?.sales_order?.create?.details?.id) }}>{ data?.sales_order?.create?.details?.sales_order_number } </span> was processed under invoice { data?.sales_order?.create?.details?.invoice_number }.
                                                        </>
                                                    )
                                                ) : (
                                                    'No details'
                                                )
                                            }
                                            
                                        </div>
                                    </li>,
                                     <li className={`rb-item timeline-progress-layer-text ${ data?.sales_order?.approved?.status && 'active' }`} ng-repeat="itembx">
                                        <div className="timestamp">
                                            <div className="drawer-title-container">{ data?.sales_order?.approved?.status ? data?.sales_order?.approved?.title : 'Approved' }</div>
                                            {
                                                data?.sales_order?.approved?.status && (
                                                        <div className="drawer-date-container">{ moment(data?.sales_order?.approved?.date).format(CommonDateOnlyFormat) }</div>
                                                )
                                            }
                                        </div>
                                        <div className="item-content">{ data?.sales_order?.approved?.status ? `${data?.sales_order?.approved?.message}.` : 'No details' }</div>
                                    </li>,
                                    source === 'sales_order' && (
                                        <>
                                            <li className={`rb-item timeline-progress-layer-text ${ data?.sales_order?.billed?.status && 'active' }`} ng-repeat="itembx">
                                                <div className="timestamp">
                                                    <div className="drawer-title-container">{ data?.sales_order?.billed?.status ? data?.sales_order?.billed?.title : 'Sales order billed' }</div>
                                                    {
                                                        data?.sales_order?.billed?.status && (
                                                            <div className="drawer-date-container">{ moment(data?.sales_order?.billed?.date).format(CommonDateOnlyFormat) }</div>
                                                        )
                                                    }
                                                </div>
                                                <div className="item-content">
                                                    { 
                                                        data?.sales_order?.billed?.status ? (
                                                            <>
                                                                Invoice <span className="text-primary cursor-pointer" onClick={() => { viewData('invoice', data?.sales_order?.billed?.details?.id) }}>{data?.sales_order?.billed?.details?.invoice_number}</span> created.
                                                            </>
                                                        ) : (
                                                            'No details'
                                                        ) 
                                                    }
                                                </div>
                                            </li>
                                            
                                        </>
                                    )
                                ].filter(Boolean);

                                if (!showAllSalesOrder) {
                                    return items.slice(0, 1);
                                }
                                return items;
                            })()}
                                </ul>

                                <div className="absolute" style={{ marginTop: '-20px', left: '27px' }}>
                                    <a href="javascript:void(0);"
                                        style={{ fontSize: '12px', padding: 0 }}
                                        onClick={() => setShowAllSalesOrder(!showAllSalesOrder)}
                                    >
                                        {showAllSalesOrder ? 'View less' : 'View more'}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: 'preparing',
            arrangement: 4,
            content: (
                <div className="mt10">

                    <div className="row">
                        <div className={`col-12 flex gap-3 align-items-center mb10 timeline-section-header  ${ data?.operation?.processing?.status && 'active' }`}>
                            <div>
                                <img src={PreparingImageIcon} height={'40px'} />
                            </div>
                            <div className="drawer-title-container" style={{ marginLeft: '10px', letterSpacing: '0.3px' }}>
                                Preparing
                            </div>
                            
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                                        
                            <div className="rb-container">
                                <ul className="rb">
                                    {(() => {
                                        const items = [
                                            <li className={`rb-item timeline-progress-layer-text ${ data?.operation?.processing?.status && 'active' }`} ng-repeat="itembx">
                                                <div className="timestamp">
                                                    <div className="drawer-title-container">{ data?.operation?.processing?.status ? data?.operation?.processing?.title : 'Order processed' }</div>
                                                    {
                                                        data?.operation?.processing?.status && (
                                                            <div className="drawer-date-container">{ moment(data?.operation?.processing?.date).format(CommonDateOnlyFormat) }</div>
                                                        )
                                                    }
                                                </div>
                                                <div className="item-content text-gray-2">
                                                    {data?.operation?.processing?.status ? 'Order started to process' : 'No details'}
                                                </div>
                                            </li>,

                                            data?.operation?.processing?.purchase_orders?.length > 0 && (
                                                <>
                                                    {
                                                        data?.operation?.processing?.purchase_orders?.map((po, index) => (
                                                            <>
                                                                <li className={`rb-item timeline-progress-layer-text active`} ng-repeat="itembx">
                                                                    <div className="timestamp">
                                                                        <div className="drawer-title-container">
                                                                            {po?.title}
                                                                            {
                                                                                po?.status === 'pending' ? (
                                                                                    <div className={`badge badge-danger timeline-progress-badge`} style={{ width: 'auto', marginLeft: `${isMobile ? '' : '10px'}`, marginTop: `${isMobile ? '5px' : ''}`, marginBottom: `${isMobile ? '5px' : ''}` }}>
                                                                                        <span className="fw-300">Pending QTY</span> 
                                                                                        <span style={{ marginLeft: '5px', fontWeight: '600' }}>{po?.quantity_comparison}</span>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className={`badge badge-success timeline-progress-badge`} style={{ width: 'auto', marginLeft: `${isMobile ? '' : '10px'}`, marginTop: `${isMobile ? '5px 0' : ''}`, marginBottom: `${isMobile ? '5px' : ''}` }}>
                                                                                        <span className="fw-300">Purchased QTY</span> 
                                                                                        <span style={{ marginLeft: '5px', fontWeight: '600' }}>{po?.quantity_comparison}</span>
                                                                                    </div>
                                                                                )
                                                                            }
                                                                        </div>

                                                                    </div>
                                                                    <div className="item-content text-gray-2">
                                                                        Created on { moment(po?.date).format(CommonDateOnlyFormat) }
                                                                    </div>
                                                                    <div className="item-content text-gray-2">
                                                                        {
                                                                            po?.details?.map((item, index) => (
                                                                                <div>
                                                                                    {item?.qty} pcs of {item?.product} has been ordered from  <span className="text-primary cursor-pointer" onClick={() => { viewData('contacts', item?.supplier_id) }}>{item?.supplier} </span> with the amount of {item?.currency} {item?.total}
                                                                                </div>
                                                                            ))
                                                                        }
                                                                    </div>
                                                                </li>
                                                                {
                                                                    po?.payments?.length > 0 && (
                                                                        po?.payments?.map((pop, index) => (
                                                                            <li className={`rb-item timeline-progress-layer-text active`} ng-repeat="itembx">
                                                                                <div className="timestamp">
                                                                                    <div className="drawer-title-container">
                                                                                        Payment for {' '}
                                                                                        <span 
                                                                                            className="text-primary"
                                                                                        >
                                                                                            {pop?.reference_no}
                                                                                        </span> created.
                                                                                    </div>
                                                                                </div>
                                                                                <div className="item-content text-gray-2">
                                                                                    <div>
                                                                                        Payment registered for the amount of {pop?.currency} {pop?.total} using {pop?.payment_method}.
                                                                                    </div>
                                                                                    <div>
                                                                                        {
                                                                                            pop?.filename && (
                                                                                                <button 
                                                                                                    className="btn btn-primary btn-outlined-main white-var"
                                                                                                    onClick={() => {
                                                                                                        window.open(pop?.attachment_url, '_blank')
                                                                                                    }}
                                                                                                >
                                                                                                    <AttachmentIcon width={'12px'} height={'12px'} />
                                                                                                    {pop?.filename}
                                                                                                </button>
                                                                                            )
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                            </li>
                                                                        ))
                                                                    )
                                                                }
                                                            </>
                                                        ))
                                                    }
                                                </>
                                            ),

                                            data?.operation?.picklist?.picklists?.length > 0 && (
                                                <>
                                                    {
                                                        data?.operation?.picklist?.picklists?.map((pl, index) => (
                                                            <>
                                                                <li className={`rb-item timeline-progress-layer-text active`} ng-repeat="itembx">
                                                                    <div className="timestamp">
                                                                        <div className="drawer-title-container">{pl?.title}</div>
                                                                    </div>
                                                                    <div className="item-content text-gray-2">
                                                                        {
                                                                            pl?.details?.map((item, index) => (
                                                                                item?.qty > 0 && (
                                                                                    <div>
                                                                                        {item?.qty} pcs of {item?.product} has been collected and waiting to ship.
                                                                                    </div>
                                                                                )
                                                                            ))
                                                                        }
                                                                    </div>
                                                                </li>
                                                            </>
                                                        ))
                                                    }
                                                </>
                                            ),
                                            <li className={`rb-item timeline-progress-layer-text ${ data?.operation?.picklist?.status && 'active' }`} ng-repeat="itembx">
                                                <div className="timestamp">
                                                    <div className="drawer-title-container">{ data?.operation?.picklist?.status ? data?.operation?.picklist?.title : 'Waiting for shipment' }</div>
                                                    {
                                                        data?.operation?.picklist?.status && (
                                                            <div className="drawer-date-container">{ moment(data?.operation?.picklist?.date).format(CommonDateOnlyFormat) }</div>
                                                        )
                                                    }
                                                </div>
                                                <div className="item-content">{ data?.operation?.picklist?.status ? data?.operation?.picklist?.message : 'No details' }</div>
                                            </li>
                                        ].filter(Boolean);

                                        if (!showAllPreparing) {
                                            return items.slice(0, 1);
                                        }
                                        return items;
                                    })()}




                                </ul>
                                <div className="absolute" style={{ marginTop: '-20px', left: '27px' }}>
                                    <a href="javascript:void(0);"
                                        style={{ fontSize: '12px', padding: 0 }}
                                        onClick={() => setShowAllPreparing(!showAllPreparing)}
                                    >
                                        {showAllPreparing ? 'View less' : 'View more'}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: 'shipped',
            arrangement: 5,
            content: (
                
                <div className="mt10">

                    <div className="row">
                        <div className={`col-12 flex gap-3 align-items-center mb10 timeline-section-header ${ data?.operation?.shipment?.status && 'active' }`}>
                            <div>
                                <img src={ShipmentImageIcon} height={'40px'} />
                            </div>
                            <div className="drawer-title-container" style={{ marginLeft: '10px', letterSpacing: '0.3px' }}>
                                

                                {
                                    shippedQuantity > 0 ? (
                                        shippedQuantity < totalOrderQuantity ? 'Partially Shipped' : 'Shipped'
                                    ) : (
                                        'Shipped'     
                                    )
                                }
                            </div>
                            
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                                        
                            <div className="rb-container">
                                <ul className="rb">
                                    {(() => {
                                        const items = [
                                            <li className={`rb-item timeline-progress-layer-text ${ data?.operation?.shipment?.status && 'active' } `} ng-repeat="itembx">
                                                <div className="timestamp">
                                                    <div className="drawer-title-container">{ data?.operation?.shipment?.status ? data?.operation?.shipment?.title : 'Order shipped' }</div>
                                                    {
                                                        data?.operation?.shipment?.status && (
                                                            <div className="drawer-date-container">{ moment(data?.operation?.shipment?.date).format(CommonDateOnlyFormat) }</div>
                                                        )
                                                    }
                                                </div>
                                                <div className="item-content">{ data?.operation?.shipment?.status ? data?.operation?.shipment?.message : 'No details' }</div>
                                            </li>,

                                            data?.operation?.shipment?.shipments?.length > 0 && (
                                                data?.operation?.shipment?.shipments?.map((shipment, index) => (
                                                    <li className={`rb-item timeline-progress-layer-text ${ data?.operation?.shipment?.status && 'active' } `} ng-repeat="itembx"> 
                                                        <div className="timestamp">
                                                            <div className="drawer-title-container">Shipped via {shipment?.shipping_company}</div>
                                                        </div>
                                                        <div className="item-content text-gray-2">
                                                            <div>
                                                                {shipment?.shipping_company} has been selected as shipping provider to deliver the order. Track order {' '}
                                                                <span  className="cursor-pointer text-primary" onClick={() => { window.open(shipment?.tracking_url, '_blank') }} >
                                                                    here
                                                                </span>.
                                                                
                                                            </div>
                                                            
                                                            <div>
                                                                {
                                                                    shipment?.filename && (
                                                                        <button 
                                                                            className="btn btn-primary btn-outlined-main white-var"
                                                                            onClick={() => {
                                                                                window.open(shipment?.attachment_url, '_blank')
                                                                            }}
                                                                        >
                                                                            <AttachmentIcon width={'12px'} height={'12px'} />
                                                                            {shipment?.filename}
                                                                        </button>
                                                                    )
                                                                }
                                                            </div>
                                                        </div>
                                                    </li>
                                                ))
                                            ),
                                        ].filter(Boolean);

                                        if (!showAllShipments) {
                                            return items.slice(0, 1);
                                        }
                                        return items;
                                    })()}
                                    

                                </ul>

                                {
                                    data?.operation?.shipment?.shipments?.length > 0 && (
                                        <div className="absolute" style={{ marginTop: '-20px', left: '27px' }}>
                                            <a href="javascript:void(0);"
                                                style={{ fontSize: '12px', padding: 0 }}
                                                onClick={() => setShowAllShipments(!showAllShipments)}
                                            >
                                                {showAllShipments ? 'View less' : 'View more'}
                                            </a>
                                        </div>
                                    )
                                }

                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    const sortedTimelineSections = timelineSections.sort((a, b) => a.arrangement - b.arrangement);

	return (
		<>
            <div className="timeline-accordion">
                
                <Accordion  activeKey={isOpen ? "0" : null}>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header
                            className={`timeline-accordion-header ${isOpen ? 'closed' : 'open'}`}
                            onClick={() => {
                                if (isMobile) {
                                    handleAccordionToggle()
                                }
                            }}
                        >

                            {
                                isDataLoaded ? (
                                    <div className="order-timeline-progress-card">
                                        <div className="order-timeline-progress-header">
                                            <div>
                                                <TimelineTitleLoaderIcon width={'20px'} height={'20px'} />
                                            </div>
                                            <h2>Order status</h2>
                                            {
                                                !isMobile && (
                                                    <div>

                                                        <button
                                                            className="btn btn-primary btn-outlined-main white-var timeline-view-more-btn"
                                                            style={{ minWidth: 'unset', paddingBlock: '4.1px', marginLeft: '10px' }}
                                                            onClick={() => {
                                                                handleAccordionToggle()
                                                            }}
                                                        >
                                                            <span style={{ fontSize: '1.1em' }}>
                                                                { isOpen ? 'View less' : 'View more' }
                                                            </span>{' '}
                                                            <span
                                                                className="d-flex"
                                                                style={{ transition: '0.3s ease-in-out', ...(!isOpen ? { transform: 'rotate(180deg)' } : {}) }}
                                                            >
                                                                <MdKeyboardArrowDown />
                                                            </span>
                                                        </button>
                                                    </div>
                                                )
                                            }
                                            {
                                                productOverviewCount > 0 && (
                                                    pendingQty > 0 && (
                                                        <div
                                                            className={`badge badge-danger absolute cursor-pointer`}
                                                            style={{ width: 'auto', right: '0' }}
                                                            onClick={() => {
                                                                // setShowPurchaseAdvice(true)
                                                            }}
                                                        >
                                                            Pending QTY {pendingQty}
                                                        </div>
                                                    )
                                                )
                                            }
                                        </div>

                                        <div className="order-timeline-progress-steps">
                                            {sortedSteps.map((step, idx) => (
                                                <div
                                                    key={step.key}
                                                    className={`order-timeline-progress-step cursor-pointer ${step.active ? 'active' : ''}`}
                                                    onClick={() => {
                                                        if (!isOpen) {
                                                            handleAccordionToggle()
                                                        }
                                                        if (step.label === 'Sales Order') {
                                                            setShowAllSalesOrder(true);
                                                        } else if (step.label === 'Invoice') {
                                                            setShowAllInvoices(true);
                                                        } else if (step.label === 'Paid') {
                                                            setShowAllPayments(true);
                                                        }
                                                    }}
                                                >
                                                    <div className="order-timeline-progress-icon">
                                                        <div>
                                                            <img src={step.icon} className="timeline-header-icon" />
                                                        </div>
                                                    </div>
                                                    <div className="order-timeline-progress-label">{step.label}</div>
                                                </div>
                                            ))}
                                            <div
                                                className={`order-timeline-progress-step cursor-pointer  ${data?.operation?.processing?.status && 'active'}`}
                                                onClick={() => {
                                                    if (!isOpen) {
                                                        handleAccordionToggle()
                                                    }
                                                    setShowAllPreparing(true);
                                                }}
                                            >
                                                <div className="order-timeline-progress-icon">
                                                    <img src={PreparingImageIcon} className="timeline-header-icon" />
                                                </div>
                                                <div className="order-timeline-progress-label">Preparing</div>
                                            </div>

                                            <div
                                                className={`order-timeline-progress-step cursor-pointer ${data?.operation?.shipment?.status && 'active'}`}
                                                onClick={() => {
                                                    if (!isOpen) {
                                                        handleAccordionToggle()
                                                    }
                                                    setShowAllShipments(true);
                                                }}
                                            >
                                                <div className="order-timeline-progress-icon">
                                                    <img src={ShipmentImageIcon} className="timeline-header-icon" />
                                                </div>
                                                <div className="order-timeline-progress-label">
                                                    {
                                                        isMobile ? (
                                                            shippedQuantity > 0 ? (
                                                                shippedQuantity < totalOrderQuantity ? 'Partial' : 'Shipped'
                                                            ) : (
                                                                'Shipped'     
                                                            )
                                                        ) : (
                                                            shippedQuantity > 0 ? (
                                                                shippedQuantity < totalOrderQuantity ? 'Partially Shipped' : 'Shipped'
                                                            ) : (
                                                                'Shipped'     
                                                            )
                                                        )
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <Skeleton variant="rounded" width={'100%'} height={130} style={{ borderRadius: '12px' }} />
                                    </div>
                                )
                            }


                        </Accordion.Header>
                        <Accordion.Body>
                            <div className="row" style={{ marginTop: '-28px' }}>
                                <div className="col-12">
                                    <hr style={{ marginTop: '30px', marginBottom: '30px' }} />

                                    {
                                        data?.qoutation?.create?.status === true && (
                                            <>
                                                <div className="row drawer-container-header-row">
                                                    <div className="col-12 drawer-content-header-container">
                                                        <div className="row">
                                                            <div className="col-12 pl-20 text-primary">
                                                                Quotations
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="mt10">
                                                    <div className="row">
                                                        <div className="col-12">
                                                            
                                                            <div className="rb-container">
                                                                <ul className="rb">
                                                                    <li className={`rb-item ${ data?.qoutation?.create?.status && 'active' }`} ng-repeat="itembx">
                                                                        <div className="timestamp">
                                                                            <div className="drawer-title-container">{ data?.qoutation?.create?.status ? data?.qoutation?.create?.title : 'Quotation created' }</div>
                                                                            {
                                                                                data?.qoutation?.create?.status && (
                                                                                    <div className="drawer-date-container">{ moment(data?.qoutation?.create?.date).format(CommonDateOnlyFormat) }</div>
                                                                                )
                                                                            }
                                                                        </div>
                                                                        <div className="item-content text-gray-2">{ data?.qoutation?.create?.status ? `${data?.qoutation?.create?.message}.` : 'No details' }</div>
                                                                    </li>
                                                                    <li className={`rb-item ${ data?.qoutation?.approval?.status && 'active' }`} ng-repeat="itembx">
                                                                        <div className="timestamp">
                                                                            <div className="drawer-title-container">{ data?.qoutation?.approval?.status ? data?.qoutation?.approval?.title : 'Approval sent' }</div>
                                                                            {
                                                                                data?.qoutation?.approval?.status && (
                                                                                    <div className="drawer-date-container">{ moment(data?.qoutation?.approval?.date).format(CommonDateOnlyFormat) }</div>
                                                                                )
                                                                            }
                                                                        </div>
                                                                        <div className="item-content">{ data?.qoutation?.approval?.status ? `${data?.qoutation?.approval?.message}.` : 'No details' }</div>
                                                                    </li>
            
                                                                    <li className={`rb-item ${ data?.qoutation?.accepted?.status && 'active' }`} ng-repeat="itembx">
                                                                        <div className="timestamp">
                                                                            <div className="drawer-title-container">{ data?.qoutation?.accepted?.status ? data?.qoutation?.accepted?.title : 'Approved' }</div>
                                                                            {
                                                                                data?.qoutation?.accepted?.status && (
                                                                                    <div className="drawer-date-container">{ moment(data?.qoutation?.accepted?.date).format(CommonDateOnlyFormat) }</div>
                                                                                )
                                                                            }
                                                                        </div>
                                                                        <div className="item-content">{ data?.qoutation?.accepted?.status ? `${data?.qoutation?.accepted?.message}.` : 'No details' }</div>
                                                                    </li>
                                                                </ul>
            
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )
                                    }



                                    {sortedTimelineSections.map(section => section.content)}


                                </div>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </div>

				{showPurchaseAdvice && (
					<PurchaseAdviceModal
						show={true}
                        handleClose={() => {
                            setShowPurchaseAdvice(false);
						}}
					/>
				)}

			<div className={`timeline-overlay ${showPurchaseAdvice ? 'show' : ''}`}></div>
		</>
	);
}

export default Timeline;
